/**
 * This is the file that handles the global messages for the application.
 * It will store the messages in a map with the eventID as the key. when a new eventID is set, the messages for the old eventID will be cleared.
 * For global messages, the eventID is 0, and they are never cleared.
 *
 * This will also listen to the eventCommonStore and present warnings and errors in the UI if for example we have stations without any ALIAS
 *
 */

import { $ti } from '@/locize';
import type { IAlertMessage } from '@/objects';
import type { IRouteParams } from '@/objects/RouteParams';
import { Router, type RouteName } from '@/objects/Router';
import { useEventCommonStoreRef, useEventStoreRef } from '@/stores';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

export const useGlobalMessages = defineStore('globalmessages', () => {
    const globalMessages = ref<Map<number, IAlertMessage[]>>(new Map());

    // A reference to the eventID so we can clear the messages for the old eventID.
    const { eventID } = useEventStoreRef();

    const { Stations } = useEventCommonStoreRef();

    const allreadyProcessedMessages: Map<string, Date> = new Map();

    /**
     * Add a message to the global messages.
     * @param message
     */
    const AddMessage = (message: IAlertMessage, eventId: number) => {
        if (!globalMessages.value.has(eventId)) {
            globalMessages.value.set(eventId, []);
        }

        const msg = allreadyProcessedMessages.get(message.message);

        if (msg && msg > new Date()) {
            return;
        }

        globalMessages.value.get(eventId)?.push(message);

        // We add the message so we dont show it again for the next 5 minutes
        allreadyProcessedMessages.set(message.message, new Date(new Date().getTime() + 60000 * 5));
    };

    /**
     * Clear all messages for a specific event.
     * This happens automatically when the eventID changes.
     * This does not clear the global messages. i.e. ID 0 They are never cleared
     * @param eventId
     * @param key - Optional key to clear a specific message
     * @returns
     */
    const clearMessagesForEvent = (eventId: number | undefined, key?: string) => {
        if (eventId === undefined || !globalMessages.value.has(eventId)) return;

        if (key !== undefined) {
            // If we have a key, we only remove that message
            globalMessages.value.set(eventId, globalMessages.value.get(eventId)?.filter((m) => m.dismisskey !== key) ?? []);
        } else {
            globalMessages.value.delete(eventId);
        }
    };

    /**
     * watch the eventID and clear the messages for the old eventID
     */
    watch(eventID, (_, oldid) => clearMessagesForEvent(oldid), { immediate: true });

    const route = useRouter();

    /**
     * Station errors
     */
    watch([Stations, route.currentRoute], () => addStationMessages());

    /**
     * This should add missing alias messages for stations. but not show it on the "Dashboard" page since that one has its own logic for this.
     */
    const addStationMessages = () => {
        const currentRoute = route.currentRoute;

        clearMessagesForEvent(eventID.value, 'station-missing-alias');

        console.log('Current route name:', currentRoute);

        if (currentRoute.value.name === 'dashboard') {
            return;
        }

        // If we have any stations without an alias, we add a error message
        const stationsWithoutAlias = Stations.value.filter((s) => (s.StationAlias ?? []).length === 0);

        if (stationsWithoutAlias) {
            stationsWithoutAlias.forEach((station) =>
                AddMessage(
                    {
                        dismisskey: `station-missing-alias`,
                        type: 'danger',
                        message: $ti('station.stationmissingalias', { station: station.Name }, true),
                        hidden: false,
                        routeName: 'racesetup' as RouteName,
                        routeParams: { tab: 'general', id: station.ID } as IRouteParams,
                    } as IAlertMessage,
                    eventID.value,
                ),
            );
        }
    };

    /**
     * Internal function to add a danger message to the global messages.
     * @param message
     * @param eventId
     * @param routeName
     * @param routeParams
     * @returns
     */
    const addDangerMessage = (message: string, eventId: number, routeName?: RouteName, routeParams?: IRouteParams) =>
        AddMessage({ type: 'danger', message, hidden: false, routeName: routeName, routeParams: routeParams }, eventId);

    /**
     * Internal function to add a danger message to the global messages.
     * @param message
     * @param eventId
     * @param routeName
     * @param routeParams
     * @returns
     */
    const addWarningMessage = (message: string, eventId: number, routeName?: RouteName, routeParams?: IRouteParams) =>
        AddMessage({ type: 'warning', message, hidden: false, routeName: routeName, routeParams: routeParams }, eventId);

    return {
        /**
         * The global messages.
         */
        Messages: globalMessages,

        /**
         * Get all the warnings for the current event.
         * @returns
         */
        Warnings: (eventId: number) => globalMessages.value.get(eventId)?.filter((m) => m.hidden !== true && m.type === 'warning') ?? [],

        /**
         * Get all the errors for the current event.
         * @returns
         */
        Errors: (eventId: number) => globalMessages.value.get(eventId)?.filter((m) => m.hidden !== true && m.type === 'danger') ?? [],

        /**
         * Add a message to the global messages.
         */
        AddMessage,
    };
});
